import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { apiClient } from '../../lib/api'
import { FoliateViewer } from './FoliateViewer'
import { Button } from '../ui/button'
import { Loader2, AlertCircle, BookOpen, ArrowLeft, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { getLanguageByCode } from '../../lib/constants/languages'
import { useToast } from '../../hooks/use-toast'
import { useNavigateBack } from '../../hooks/useNavigateBack'

interface PreviewConfig {
  max_percent: number
  max_pages: number
}

export function PreviewPage() {
  const { t } = useTranslation('common')
  const { bookId, translationId } = useParams<{ bookId: string; translationId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const { toast } = useToast()
  const goBack = useNavigateBack('/library')
  
  // Check if this is user's own book (graded book)
  const isOwnedBook = (location.state as { isOwnedBook?: boolean })?.isOwnedBook ?? false
  
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Preview data
  const [bookTitle, setBookTitle] = useState('')
  const [originalLanguage, setOriginalLanguage] = useState<string | null>(null)
  const [translationLanguage, setTranslationLanguage] = useState('')
  const [cefrLevel, setCefrLevel] = useState<string | null>(null)
  const [originalStreamUrl, setOriginalStreamUrl] = useState<string | null>(null)
  const [originalFileFormat, setOriginalFileFormat] = useState<string>('epub')
  const [translatedStreamUrl, setTranslatedStreamUrl] = useState<string | null>(null)
  const [translationFileFormat, setTranslationFileFormat] = useState<string>('epub')
  const [previewConfig, setPreviewConfig] = useState<PreviewConfig | null>(null)
  
  // Preview state
  const [previewEnded, setPreviewEnded] = useState(false)
  
  // Mobile tab state
  const [activeTab, setActiveTab] = useState<'original' | 'translated'>('translated')

  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { from: `/preview/${bookId}/${translationId}` } })
      return
    }

    if (!bookId || !translationId) {
      setError(t('reader.invalidParams', 'Invalid book or translation ID'))
      setLoading(false)
      return
    }

    loadPreviewConfig()
  }, [bookId, translationId, user, navigate, t])

  const loadPreviewConfig = async () => {
    if (!bookId || !translationId) return

    try {
      setLoading(true)
      setError(null)

      // Always start preview from page 1 (no skip_intro)
      const response = await apiClient.reader.getPreviewConfig(bookId, translationId, false)

      if (!response.success) {
        setError(response.error || t('reader.previewError', 'Failed to load preview'))
        setLoading(false)
        return
      }

      setBookTitle(response.book_title ?? '')
      setOriginalLanguage(response.original_language ?? null)
      setTranslationLanguage(response.translation_language ?? '')
      setCefrLevel(response.cefr_level ?? null)
      setOriginalStreamUrl(response.original_stream_url ?? null)
      setOriginalFileFormat(response.original_file_format ?? 'epub')
      setTranslatedStreamUrl(response.translated_stream_url ?? null)
      setTranslationFileFormat(response.translation_file_format ?? 'epub')
      setPreviewConfig(response.preview_config ?? null)
      setLoading(false)
    } catch (err) {
      console.error('Failed to load preview config:', err)
      setError(t('reader.previewError', 'Failed to load preview'))
      setLoading(false)
    }
  }

  const handlePreviewEnd = useCallback(() => {
    setPreviewEnded(true)
  }, [])

  const handleClose = () => {
    // Go back to previous page in history (with fallback)
    goBack()
  }

  const handleTakeBook = () => {
    // Show toast and navigate to full reader
    if (bookId && translationId) {
      toast({
        title: t('library.bookAddedToLibrary', 'Book added to your library'),
      })
      navigate(`/read/${bookId}/${translationId}`)
    }
  }

  const getLanguageDisplayName = (langCode: string): string => {
    const lang = getLanguageByCode(langCode)
    return lang ? lang.name : langCode.toUpperCase()
  }

  if (!user) {
    return null // Will redirect in useEffect
  }

  if (loading) {
    return (
      <div className="preview-page fixed inset-0 z-50 bg-background overflow-hidden flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">{t('reader.loadingPreview', 'Loading preview...')}</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="preview-page fixed inset-0 z-50 bg-background overflow-hidden flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
          <h2 className="text-xl font-semibold mb-2">{t('reader.errorTitle', 'Unable to Load Preview')}</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={handleClose}>
            {t('common.goBack', 'Go Back')}
          </Button>
        </div>
      </div>
    )
  }

  const hasOriginal = !!originalStreamUrl
  const hasTranslated = !!translatedStreamUrl

  return (
    <div className="preview-page fixed inset-0 z-50 bg-background overflow-hidden flex flex-col">
      {/* Preview header */}
      <div className="preview-header px-4 py-2 border-b bg-card">
        <div className="flex items-center justify-between gap-3">
          {/* Left: Take book button (hidden for owned books) */}
          {!isOwnedBook && (
            <Button size="sm" onClick={handleTakeBook} className="shrink-0">
              <BookOpen className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">{t('reader.takeBook', 'Take book')}</span>
              <span className="sm:hidden">{t('reader.take', 'Take')}</span>
            </Button>
          )}
          
          {/* Center: Title */}
          <h1 className="text-base font-semibold truncate flex-1 text-center min-w-0">{bookTitle}</h1>
          
          {/* Right: Close button */}
          <Button variant="ghost" size="icon" onClick={handleClose} className="shrink-0 h-8 w-8">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Preview ended overlay - rendered via portal to avoid positioning issues */}
      {previewEnded && createPortal(
        <div className="preview-ended-overlay fixed inset-0 flex items-center justify-center bg-background/95 z-[100] p-4">
          <div className="preview-ended-content relative w-full max-w-sm sm:max-w-lg p-4 sm:p-8 bg-card rounded-lg shadow-lg">
            {/* Go back button - top left corner of modal */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setPreviewEnded(false)}
              className="preview-back-btn absolute top-2 left-2"
              aria-label={t('common.goBack', 'Go Back')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            
            {/* Close button - top right corner of modal */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleClose}
              className="preview-close-btn absolute top-2 right-2"
              aria-label={t('common.close', 'Close')}
            >
              <X className="h-5 w-5" />
            </Button>
            
            <div className="text-center pt-6">
              <BookOpen className="h-12 w-12 sm:h-16 sm:w-16 mx-auto mb-3 sm:mb-4 text-primary" />
              <h2 className="text-xl sm:text-2xl font-bold mb-2">{t('reader.previewEnded', 'Preview fragment ended')}</h2>
              <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6">
                {isOwnedBook 
                  ? t('reader.previewEndedOwnedDescription', 'Open the full reader to continue reading')
                  : t('reader.previewEndedDescription', 'Take book to your library to read or download')
                }
              </p>
              <div className="flex flex-col gap-3 sm:gap-4">
                {isOwnedBook ? (
                  /* For owned books - Open full reader */
                  <Button onClick={() => navigate(`/read/${bookId}/${translationId}`, { state: { from: 'user-book' } })} size="lg" className="w-full">
                    <BookOpen className="h-4 w-4 mr-2" />
                    {t('reader.openReader', 'Open Reader')}
                  </Button>
                ) : (
                  /* Primary action - Take Book */
                  <Button onClick={handleTakeBook} size="lg" className="w-full">
                    <BookOpen className="h-4 w-4 mr-2" />
                    {t('reader.takeBook', 'Take book')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Mobile: Tab navigation */}
      {hasOriginal && hasTranslated && (
        <div className="mobile-tabs lg:hidden flex border-b bg-card">
          <button
            onClick={() => setActiveTab('original')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
              activeTab === 'original'
                ? 'border-b-2 border-primary text-primary bg-primary/5'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('reader.original', 'Original')}
            {originalLanguage && ` (${getLanguageDisplayName(originalLanguage)})`}
          </button>
          <button
            onClick={() => setActiveTab('translated')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
              activeTab === 'translated'
                ? 'border-b-2 border-primary text-primary bg-primary/5'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {getLanguageDisplayName(translationLanguage)}
            {cefrLevel && ` (${cefrLevel})`}
          </button>
        </div>
      )}

      {/* Mobile: Both readers mounted but only active one visible (preserves position) */}
      {hasOriginal && hasTranslated && (
        <div className="mobile-reader lg:hidden flex-1 flex flex-col relative">
          <div className={`absolute inset-0 ${activeTab === 'original' ? 'z-10' : 'z-0 invisible'}`}>
            <FoliateViewer
              url={`${window.location.origin}${originalStreamUrl}`}
              fileFormat={originalFileFormat}
              isPreview={true}
              previewStartPercent={0}
              previewMaxPercent={previewConfig?.max_percent || 10}
              onPreviewEnd={handlePreviewEnd}
              showControls={false}
              navAtEdges={true}
            />
          </div>
          <div className={`absolute inset-0 ${activeTab === 'translated' ? 'z-10' : 'z-0 invisible'}`}>
            <FoliateViewer
              url={`${window.location.origin}${translatedStreamUrl}`}
              fileFormat={translationFileFormat}
              isPreview={true}
              previewStartPercent={0}
              previewMaxPercent={previewConfig?.max_percent || 10}
              onPreviewEnd={handlePreviewEnd}
              showControls={false}
              navAtEdges={true}
            />
          </div>
        </div>
      )}

      {/* Desktop: Side-by-side viewers */}
      <div className="preview-content hidden lg:flex flex-1">
        {/* Original book viewer */}
        {hasOriginal && (
          <div className="preview-original flex-1 border-r flex flex-col">
            <div className="preview-label px-4 py-2 bg-muted text-sm font-medium border-b text-center">
              {t('reader.original', 'Original')}
              {originalLanguage && ` (${getLanguageDisplayName(originalLanguage)})`}
            </div>
            <div className="flex-1">
              <FoliateViewer
                url={`${window.location.origin}${originalStreamUrl}`}
                fileFormat={originalFileFormat}
                isPreview={true}
                previewStartPercent={0}
                previewMaxPercent={previewConfig?.max_percent || 10}
                onPreviewEnd={handlePreviewEnd}
                showControls={false}
                navAtEdges={true}
              />
            </div>
          </div>
        )}

        {/* Translated book viewer */}
        {hasTranslated && (
          <div className="preview-translated flex-1 flex flex-col">
            <div className="preview-label px-4 py-2 bg-muted text-sm font-medium border-b text-center">
              {t('reader.translated', 'Translated')}
              {` (${getLanguageDisplayName(translationLanguage)}`}
              {cefrLevel && ` - ${cefrLevel}`}
              {')'}
            </div>
            <div className="flex-1">
              <FoliateViewer
                url={`${window.location.origin}${translatedStreamUrl}`}
                fileFormat={translationFileFormat}
                isPreview={true}
                previewStartPercent={0}
                previewMaxPercent={previewConfig?.max_percent || 10}
                onPreviewEnd={handlePreviewEnd}
                showControls={false}
                navAtEdges={true}
              />
            </div>
          </div>
        )}
      </div>

      {/* Fallback if no original (show translated only in full width - both mobile and desktop) */}
      {!hasOriginal && hasTranslated && (
        <div className="preview-translated-only flex-1 flex flex-col">
          <div className="preview-label px-4 py-2 bg-muted text-sm font-medium border-b text-center">
            {t('reader.translatedPreview', 'Translation Preview')}
            {` - ${getLanguageDisplayName(translationLanguage)}`}
            {cefrLevel && ` (${cefrLevel})`}
          </div>
          <div className="flex-1">
            <FoliateViewer
              url={`${window.location.origin}${translatedStreamUrl}`}
              fileFormat={translationFileFormat}
              isPreview={true}
              previewStartPercent={0}
              previewMaxPercent={previewConfig?.max_percent || 10}
              onPreviewEnd={handlePreviewEnd}
              showControls={false}
              navAtEdges={true}
            />
          </div>
        </div>
      )}

      {/* No content available */}
      {!hasOriginal && !hasTranslated && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">{t('reader.noPreviewContent', 'Preview not available for this book')}</p>
            <Button onClick={handleClose} className="mt-4">
              {t('common.goBack', 'Go Back')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

